// Привязка Telegram-чата к личному аккаунту (TG-ADR-3, стороны сайта).
//
// Здесь обе половины связки:
//  - код «чат → сайт»: человек пришёл по ссылке бота (`?tg_link=<код>`); код
//    ждёт входа в sessionStorage и гасится сразу после аутентификации — форма
//    входа и регистрации при этом остаются обычными;
//  - код «сайт → чат»: кнопка в настройках выдаёт deep-link на бота, а факт
//    привязки подтверждается опросом статуса — привязку из чата человек тоже
//    увидит здесь (L3: должно быть видно, какой Telegram привязался).
//
// Модуль не импортирует auth.ts: это composition root (ADR-002 phase C), рёбра
// в него закрывали hub-циклы. Признак «человек вошёл» — state.currentUser.

import { state } from './shared/state';
import { $, showToast } from './shared/index';

const TG_LINK_CODE_KEY = 'avgexpert_tg_link_code';
const LINK_POLL_MS = 4000;

interface LinkStatusResponse {
  linked?: boolean;
  telegram_id?: string | null;
}

interface LinkCodeResponse {
  code?: string;
  deep_link?: string;
  /** Миллисекунды epoch — так отдаёт link.routes. */
  expires_at?: number;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Перехват `?tg_link=<код>` — строго до обработки `?token=` в checkAuth:
 * та вычищает строку запроса целиком, и пришедший вместе с OAuth-токеном код
 * иначе пропал бы. Код одноразовый и ждёт входа в sessionStorage — вкладке
 * достаточно, а в соседние приватные данные не утекают.
 */
export function captureTelegramLinkParam(): void {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('tg_link');
  if (!code) return;
  sessionStorage.setItem(TG_LINK_CODE_KEY, code);
  params.delete('tg_link');
  const query = params.toString();
  const newUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
  window.history.replaceState({}, document.title, newUrl);
}

/**
 * Гашение отложенного кода — сразу после аутентификации (вход, регистрация или
 * живой токен при загрузке). Код снимается из хранилища до запроса: сервер
 * жжёт его при первой проверке, повторный POST дал бы только лишний 410.
 */
export async function consumePendingTelegramLinkCode(): Promise<void> {
  const code = sessionStorage.getItem(TG_LINK_CODE_KEY);
  if (!code || !state.authToken || !state.currentUser) return;
  sessionStorage.removeItem(TG_LINK_CODE_KEY);
  try {
    const r = await fetch('/api/messenger/link/consume', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + state.authToken,
      },
      body: JSON.stringify({ code }),
    });
    if (r.ok) {
      showToast('Telegram привязан к аккаунту — вернитесь в чат');
      void refreshTelegramLinkCard();
    } else if (r.status === 410) {
      showToast('Код устарел — запросите новую ссылку у бота командой /link', 'error');
    } else if (r.status === 409) {
      showToast('Этот Telegram уже привязан к другому аккаунту', 'error');
    } else if (r.status === 403) {
      showToast('Привязка Telegram доступна после регистрации', 'error');
    } else {
      showToast('Не удалось привязать Telegram — попробуйте позже', 'error');
    }
  } catch {
    // Сеть моргнула — код возвращаем: следующий вход попробует снова.
    sessionStorage.setItem(TG_LINK_CODE_KEY, code);
    showToast('Ошибка сети — привязка Telegram не подтверждена', 'error');
  }
}

async function fetchLinkStatus(): Promise<LinkStatusResponse | null> {
  if (!state.authToken) return null;
  try {
    const r = await fetch('/api/messenger/link', {
      headers: { 'Authorization': 'Bearer ' + state.authToken },
    });
    if (!r.ok) return null;
    return await r.json() as LinkStatusResponse;
  } catch {
    return null;
  }
}

function setStatus(text: string): void {
  const status = $('telegram-link-status');
  if (status) status.textContent = text;
}

function hideDeepLink(): void {
  const holder = $('telegram-link-deeplink');
  if (!holder) return;
  holder.textContent = '';
  holder.classList.add('hidden');
}

function showDeepLink(url: string): void {
  const holder = $('telegram-link-deeplink');
  if (!holder) return;
  holder.textContent = '';
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener';
  anchor.textContent = url;
  holder.appendChild(anchor);
  holder.classList.remove('hidden');
}

function renderUnlinked(): void {
  const linkBtn = $<HTMLButtonElement>('telegram-link-btn');
  if (linkBtn) {
    linkBtn.classList.remove('hidden');
    linkBtn.disabled = false;
  }
  $('telegram-unlink-btn')?.classList.add('hidden');
  hideDeepLink();
  setStatus('Привяжите Telegram, чтобы разговаривать с Аватаром прямо в чате и продолжать один разговор на сайте и в Telegram.');
}

function renderLinked(telegramId?: string | null): void {
  $('telegram-link-btn')?.classList.add('hidden');
  $('telegram-unlink-btn')?.classList.remove('hidden');
  hideDeepLink();
  setStatus(telegramId
    ? `Привязан Telegram (ID ${telegramId}).`
    : 'Telegram привязан к аккаунту.');
}

export function stopTelegramLinkPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/**
 * Ожидание подтверждения из чата. Раз в 4 секунды спрашиваем статус: привязку
 * создаёт бот по /start из deep-link, сайту о ней никто не сообщит. Дедлайн —
 * `expires_at` кода: после него бот ссылку уже не примет, опрос впустую.
 */
function startLinkPolling(expiresAt?: number): void {
  stopTelegramLinkPolling();
  const deadline = typeof expiresAt === 'number' ? expiresAt : NaN;
  pollTimer = setInterval(async () => {
    if (Number.isFinite(deadline) && Date.now() > deadline) {
      stopTelegramLinkPolling();
      renderUnlinked();
      setStatus('Срок действия ссылки истёк — нажмите «Привязать Telegram» ещё раз.');
      return;
    }
    const status = await fetchLinkStatus();
    if (status?.linked) {
      stopTelegramLinkPolling();
      renderLinked(status.telegram_id);
      showToast('Telegram привязан к аккаунту');
    }
  }, LINK_POLL_MS);
}

async function requestLinkCode(): Promise<void> {
  if (!state.authToken) return;
  const linkBtn = $<HTMLButtonElement>('telegram-link-btn');
  if (linkBtn) linkBtn.disabled = true;
  try {
    const r = await fetch('/api/messenger/link-code', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + state.authToken },
    });
    if (!r.ok) {
      if (r.status === 503) showToast('Канал Telegram сейчас недоступен — попробуйте позже', 'error');
      else if (r.status === 403) showToast('Привязка Telegram доступна после регистрации', 'error');
      else showToast('Не удалось получить ссылку для привязки', 'error');
      if (linkBtn) linkBtn.disabled = false;
      return;
    }
    const d = await r.json() as LinkCodeResponse;
    if (!d.deep_link) {
      showToast('Не удалось получить ссылку для привязки', 'error');
      if (linkBtn) linkBtn.disabled = false;
      return;
    }
    // Бота открываем сразу, но ссылку оставляем и текстом: попап могли
    // заблокировать, а с телефона её удобнее открыть самому.
    window.open(d.deep_link, '_blank', 'noopener');
    showDeepLink(d.deep_link);
    setStatus('Откройте ссылку в Telegram и нажмите «Start» — привязка подтвердится здесь сама.');
    startLinkPolling(d.expires_at);
  } catch {
    showToast('Ошибка сети — не удалось получить ссылку', 'error');
    if (linkBtn) linkBtn.disabled = false;
  }
}

async function unlinkTelegram(): Promise<void> {
  if (!state.authToken) return;
  if (!window.confirm('Отвязать Telegram от аккаунта? Разговор в чате перестанет быть связан с вашим аккаунтом.')) return;
  try {
    const r = await fetch('/api/messenger/link', {
      method: 'DELETE',
      headers: { 'Authorization': 'Bearer ' + state.authToken },
    });
    if (r.ok) {
      renderUnlinked();
      showToast('Telegram отвязан от аккаунта');
    } else {
      showToast('Не удалось отвязать Telegram', 'error');
    }
  } catch {
    showToast('Ошибка сети — Telegram не отвязан', 'error');
  }
}

/**
 * Актуализация карточки «Telegram» в настройках. Гостям карточка не
 * показывается вовсе: Free-аккаунта, к которому можно привязать чат, нет
 * (ADR-003) — сервер на это отвечает 403, а мы просто прячем блок.
 */
export async function refreshTelegramLinkCard(): Promise<void> {
  const card = $('telegram-link-card');
  if (!card) return;
  stopTelegramLinkPolling();
  if (!state.authToken || !state.currentUser) {
    card.classList.add('hidden');
    return;
  }
  const status = await fetchLinkStatus();
  if (!status) {
    card.classList.add('hidden');
    return;
  }
  if (status.linked) renderLinked(status.telegram_id);
  else renderUnlinked();
  card.classList.remove('hidden');
}

export function initTelegramLink(): void {
  $('telegram-link-btn')?.addEventListener('click', () => { void requestLinkCode(); });
  $('telegram-unlink-btn')?.addEventListener('click', () => { void unlinkTelegram(); });
}
